/**
 * SampleDataTabContent
 * ==========================================================================
 * The "Sample Data" tab of the UnifiedImportWizard (authoring flow). Owns:
 *
 *   1. Drag-and-drop upload of a sample file (CSV / XLSX / GPKG / GeoJSON /
 *      JSON / zipped shapefile). Calls the injected `parseFile` adapter
 *      which is expected to return the canonical 100-row sample.
 *
 *   2. Sheet selector (for multi-sheet Excel files) — re-parses on change.
 *
 *   3. A 100-row preview table so the author can see their data before
 *      mapping. Columns are rendered in header order; cells render as
 *      strings with truncation so wide GIS blobs don't blow up the layout.
 *
 *   4. "Detected column matches" block — as soon as parsing completes the
 *      component kicks off `suggestMappings` (AI + rule tier). Each
 *      suggestion is shown with a confidence badge, a ? accept button,
 *      and an X dismiss button. When at least one High-confidence
 *      suggestion exists, an "Accept all high-confidence" button bulk-
 *      applies them. Accepting a suggestion updates `state.fieldMappings`
 *      on the parent so the author arrives at the Field Mappings tab with
 *      most rows already filled in.
 *
 * What lives ELSEWHERE:
 *   - Grouped target field catalog + source-column combobox + transform
 *     dropdown + default-value mode all live on the Field Mappings tab
 *     (RunWizardColumnMappingPanel). This tab only deals with parsing +
 *     first-pass matching; the rich editing UX is the next step.
 *
 * Parent wiring (UnifiedImportWizard / AuthoringFlow):
 *   - Caches `availableColumns` + `sampleRows` in its own useState so the
 *     Field Mappings tab can read them without re-parsing. This component
 *     bubbles changes up via `onFileParsed` and `onFileSelected`.
 *   - Caches the raw File reference via `onFileSelected` so the Card 3
 *     save-and-import path can hand it off to the run wizard without a
 *     re-upload.
 */

import { memo, startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, FileUp, FileCheck2, Loader2, Sparkles, Check, X, CheckCheck,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import type { FieldMapping, ImportDataMode } from '@firesquirrel/local-db';
import type { TemplateEditorState } from '../templateEditor/types';
import { getTargetFieldLabel, resolveTargetFieldValue } from '../utils/targetFieldOptions';
import { sortRowsByCompleteness } from '../utils/sampleRowSort';
import { getSuggestionMappingSource, type ParseResult, type MappingSuggestion } from './FieldMappingsTabContent';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SampleDataTabContentProps {
  state: TemplateEditorState;
  onChange: (patch: Partial<TemplateEditorState>) => void;
  errors?: string[];
  /** Currently-cached parse output — kept in the parent so it survives
      tab switches. If undefined the drop-zone is shown standalone. */
  availableColumns?: string[];
  sampleRows?: Record<string, unknown>[];
  restoredParseResult?: ParseResult;
  /** Dismissed source columns from a previous session — restored from the
      persisted draft so the author's rejections survive a hard refresh. */
  restoredDismissed?: string[];
  suppressInitialAutoSuggest?: boolean;
  /** Fires after a successful parse so the parent can cache the result. */
  onFileParsed?: (
    columns: string[],
    rows: Record<string, unknown>[],
    meta: { totalRows: number; fileType: string; activeSheet?: string; availableSheets?: string[] },
  ) => void;
  /** Fires as soon as the user selects a file (before parse completes)
      so the host can capture the File reference for later reuse. */
  onFileSelected?: (file: File) => void;
  /** Adapter-injected parser. Implemented by the host as the
      upload → createPreviewJob → pollPreviewJob pipeline. SampleSize
      is the preview cap (canonical limit is 100). `onProgress` fires during
      both the upload phase (pct 0-100) and the preview-job phase
      (status strings like "queued" / "running"); the component uses
      it to drive the inline progress indicator so authors see the
      wizard is doing work even on large files. */
  parseFile?: (
    file: File,
    sampleSize: number | undefined,
    sheetName: string | undefined,
    onProgress?: (event:
      | { kind: 'upload'; pct: number }
      | { kind: 'job'; status: string }
    ) => void,
  ) => Promise<ParseResult>;
  /** Previously-generated AI/rule suggestions restored from the persisted draft so
      the author does not wait for another AI round-trip after a hard refresh. */
  restoredSuggestions?: MappingSuggestion[];
  /** Adapter-injected AI/rule suggester. */
  suggestMappings?: (params: {
    dataMode: string;
    columns: string[];
    sampleRows?: Record<string, unknown>[];
    tier?: string;
  }) => Promise<{ suggestions: MappingSuggestion[] }>;
}

// ---------------------------------------------------------------------------
// Preview sample size — mirrors the backend canonical parser cap.
// ---------------------------------------------------------------------------
const PREVIEW_SAMPLE_SIZE = 100;

interface PreviewCell {
  full: string;
  truncated: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ConfidenceBadge({ confidence }: { confidence: number }) {
  if (confidence >= 0.8) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-green-100 text-green-700">
        High
      </span>
    );
  }
  if (confidence >= 0.5) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-yellow-100 text-yellow-700">
        Review
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-gray-100 text-gray-500">
      Low
    </span>
  );
}

function getSuggestionSourceColumns(suggestion: MappingSuggestion): string[] {
  return suggestion.sourceColumns ?? [suggestion.sourceColumn];
}

function cellToString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SampleDataTabContent({
  state,
  onChange,
  errors = [],
  availableColumns: availableColumnsProp,
  sampleRows: sampleRowsProp,
  restoredParseResult,
  restoredSuggestions,
  restoredDismissed,
  suppressInitialAutoSuggest = false,
  onFileParsed,
  onFileSelected,
  parseFile,
  suggestMappings,
}: SampleDataTabContentProps) {
  // ---- Local file/parse state ----
  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(
    restoredParseResult
      ? restoredParseResult
      : availableColumnsProp && availableColumnsProp.length
      ? {
          fileType: '',
          columns: availableColumnsProp,
          sampleRows: sampleRowsProp ?? [],
          totalRows: (sampleRowsProp ?? []).length,
        }
      : null,
  );
  const [fileParseLoading, setFileParseLoading] = useState(false);
  const [fileParseError, setFileParseError] = useState<string | null>(null);
  // `parseProgress` powers the drop-zone status line. The pipeline emits
  // either `{ kind: 'upload', pct }` while bytes are being streamed to
  // Blob Storage (0-100) or `{ kind: 'job', status }` once the preview
  // job is queued and the worker is chewing through the file.
  const [parseProgress, setParseProgress] = useState<
    | { kind: 'upload'; pct: number }
    | { kind: 'job'; status: string }
    | null
  >(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!parseFile) return;

    const guardFileDrop = (event: DragEvent) => {
      if (!event.dataTransfer?.types?.includes('Files')) return;
      event.preventDefault();
    };

    document.addEventListener('dragover', guardFileDrop);
    document.addEventListener('drop', guardFileDrop);

    return () => {
      document.removeEventListener('dragover', guardFileDrop);
      document.removeEventListener('drop', guardFileDrop);
    };
  }, [parseFile]);

  useEffect(() => {
    if (!restoredParseResult) return;
    setParseResult(restoredParseResult);
  }, [restoredParseResult]);

  // ---- AI suggestion state ----
  const [suggestions, setSuggestions] = useState<MappingSuggestion[]>(
    restoredSuggestions ?? [],
  );
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(
    () => new Set(restoredDismissed ?? []),
  );

  // Restore dismissed columns from the persisted draft on mount / prop change.
  useEffect(() => {
    if (!restoredDismissed?.length) return;
    setDismissed(new Set(restoredDismissed));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredDismissed]);

  // Restore suggestions from the persisted draft when the prop arrives
  // (same pattern as restoredParseResult — the prop may arrive after mount
  // when the template is fetched async).
  useEffect(() => {
    if (!restoredSuggestions?.length) return;
    setSuggestions(restoredSuggestions);
  }, [restoredSuggestions]);

  // ---- Derived: effective columns/rows ----
  // Stabilized with useMemo: the `?? []` fallback was producing a new empty
  // array reference every render when the prop was undefined, which cascaded
  // through previewRows / previewCellStrings memos and defeated the memo
  // barriers on the preview table. Now the reference is stable across
  // renders that don't change the underlying parse data.
  const availableColumns = useMemo(
    () => (parseResult?.columns?.length
      ? parseResult.columns
      : (availableColumnsProp ?? [])),
    [parseResult, availableColumnsProp],
  );
  const sampleRows = useMemo(
    () => (parseResult?.sampleRows?.length
      ? parseResult.sampleRows
      : (sampleRowsProp ?? [])),
    [parseResult, sampleRowsProp],
  );

  // Columns already claimed by some field mapping — shown as a ? in the
  // detected-matches list so the author can see progress.
  // Memoized: without this, clicking a suggestion accept triggers a parent
  // re-render which rebuilds these Sets, which invalidates the preview
  // table's memoization and re-runs cellToString for every cell (100 rows x
  // ~100 cols on Regrid parcel files). That was the dominant cause of the
  // 800ms INP on the green-check click path.
  const mappedSourceColumns = useMemo(
    () => new Set(
      state.fieldMappings.flatMap(m => m.sourceColumns ?? []).filter(Boolean),
    ),
    [state.fieldMappings],
  );
  const mappedTargetFields = useMemo(
    () => new Set(
      state.fieldMappings.filter(m => m.sourceColumns?.[0]).map(m => m.targetField),
    ),
    [state.fieldMappings],
  );

  const pendingSuggestions = useMemo(
    () => suggestions.filter(
      s => getSuggestionSourceColumns(s).every(col => !dismissed.has(col))
        && getSuggestionSourceColumns(s).every(col => !mappedSourceColumns.has(col))
        && !mappedTargetFields.has(s.targetField),
    ),
    [suggestions, dismissed, mappedSourceColumns, mappedTargetFields],
  );

  // ---- Parse handler ----
  const handleSampleFile = useCallback(
    async (file: File, sheetName?: string) => {
      if (!parseFile) return;
      onFileSelected?.(file);
      setFileParseLoading(true);
      setFileParseError(null);
      setParseProgress({ kind: 'upload', pct: 0 });
      try {
        const result = await parseFile(file, PREVIEW_SAMPLE_SIZE, sheetName, (ev) => {
          // Adapter emits upload percent (0-100) then job status strings
          // once the file is in Blob Storage and the worker starts
          // processing. Both go into a single state slot so we never
          // show stale "75% uploading" while the worker is parsing.
          setParseProgress(ev);
        });
        setParseResult(result);
        const cols = result.columns ?? [];
        const rows = result.sampleRows ?? [];
        onFileParsed?.(cols, rows, {
          totalRows: result.totalRows ?? rows.length,
          fileType: result.fileType ?? '',
          activeSheet: result.activeSheet,
          availableSheets: result.availableSheets,
        });
        // New parse invalidates any prior suggestions — authors expect
        // fresh matches against the newly-selected file/sheet.
        setSuggestions([]);
        setDismissed(new Set());
        onChange({ aiSuggestions: [], aiDismissed: [] });
      } catch (err: any) {
        setFileParseError(err?.message ?? 'Failed to parse file');
      } finally {
        setFileParseLoading(false);
        setParseProgress(null);
      }
    },
    [parseFile, onFileParsed, onFileSelected, onChange],
  );

  const acceptSampleFile = useCallback(
    (file: File) => {
      setSampleFile(file);
      setParseResult(null);
      handleSampleFile(file);
    },
    [handleSampleFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      acceptSampleFile(file);
    },
    [acceptSampleFile],
  );

  const handleDropZoneDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileParseLoading) return;
    setIsDragOver(true);
  }, [fileParseLoading]);

  const handleDropZoneDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDropZoneDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (fileParseLoading) return;
      const file = e.dataTransfer.files?.[0];
      if (file) acceptSampleFile(file);
    },
    [acceptSampleFile, fileParseLoading],
  );

  const handleSheetChange = useCallback(
    (sheet: string) => {
      if (!sampleFile) return;
      handleSampleFile(sampleFile, sheet);
    },
    [sampleFile, handleSampleFile],
  );

  // ---- AI suggest ----
  const runAutoSuggest = useCallback(
    async (cols: string[], rows: Record<string, unknown>[]) => {
      if (!suggestMappings || cols.length === 0) return;
      // Exclude columns the author already explicitly rejected so re-running
      // AI does not resurface dismissed suggestions.
      const effectiveCols = cols.filter(c => !dismissed.has(c));
      if (effectiveCols.length === 0) {
        setSuggestError('All columns have already been reviewed. Upload a new file to reset.');
        return;
      }
      setSuggestLoading(true);
      setSuggestError(null);
      try {
        const result = await suggestMappings({
          dataMode: state.dataMode,
          columns: effectiveCols,
          sampleRows: rows,
          tier: 'both',
        });
        setSuggestions(result.suggestions);
        // Do NOT reset dismissed here — the author's rejections must survive
        // a re-run. Dismissed columns were already filtered from effectiveCols.
        // Persist updated suggestions (dismissed array unchanged).
        onChange({ aiSuggestions: result.suggestions });
      } catch (err: any) {
        setSuggestError(err?.message ?? 'Failed to detect column matches');
      } finally {
        setSuggestLoading(false);
      }
    },
    [state.dataMode, suggestMappings, dismissed, onChange],
  );

  // Auto-run detection the moment columns appear (first parse success).
  // Guarded by a ref so we don't re-run on every onChange pass.
  const autoRanRef = useRef<string>('');
  const suppressInitialAutoSuggestRef = useRef(suppressInitialAutoSuggest);
  useEffect(() => {
    if (!parseResult) return;
    if (!suppressInitialAutoSuggestRef.current) return;
    if (sampleFile) return;
    autoRanRef.current = `${parseResult.activeSheet ?? ''}::${parseResult.columns.join('|')}`;
    suppressInitialAutoSuggestRef.current = false;
  }, [parseResult, sampleFile]);

  useEffect(() => {
    if (!parseResult) return;
    const fingerprint = `${parseResult.activeSheet ?? ''}::${parseResult.columns.join('|')}`;
    if (autoRanRef.current === fingerprint) return;
    if (!suggestMappings) return;
    autoRanRef.current = fingerprint;
    void runAutoSuggest(parseResult.columns, parseResult.sampleRows);
  }, [parseResult, runAutoSuggest, suggestMappings]);

  // ---- Suggestion accept/dismiss ----
  //
  // IMPORTANT: the ONLY urgent UI reaction to an accept/dismiss click is
  // removing the item from the AI list. That's done by updating local
  // `dismissed` state synchronously, which re-renders ONLY DetectedMatchesBlock.
  //
  // The field-mapping write lifts state into the wizard's editor state.
  // That's a wider update (the Field Mappings tab and save footer care),
  // so we wrap it in `startTransition` — React treats it as non-urgent,
  // keeps it interruptible, and the click's paint never blocks on it.
  // Subsequent clicks aren't queued behind the prior transition either —
  // React collapses them into the latest value.
  const acceptSuggestion = useCallback(
    (s: MappingSuggestion) => {
      const resolvedTargetField = resolveTargetFieldValue(s.targetField, state.dataMode);
      if (!resolvedTargetField) return;
      const cols = getSuggestionSourceColumns(s);
      // Urgent: AI list update — user sees the row vanish immediately.
      setDismissed(prev => new Set([...prev, ...cols]));
      // Deferred: lift mapping into wizard state without blocking the click.
      startTransition(() => {
        const existingIdx = state.fieldMappings.findIndex(m => m.targetField === resolvedTargetField);
        const next: FieldMapping = {
          targetField: resolvedTargetField,
          sourceColumns: cols,
          isMatchKey:
            existingIdx >= 0 ? !!state.fieldMappings[existingIdx]!.isMatchKey : false,
          transform: {
            type: (s.transform as any) ?? 'none',
            ...(s.concatSeparator ? { separator: s.concatSeparator } : {}),
          },
          confidenceScore: s.confidence,
          mappingSource: getSuggestionMappingSource(s),
        };
        const arr = [...state.fieldMappings];
        if (existingIdx >= 0) arr[existingIdx] = { ...arr[existingIdx]!, ...next };
        else arr.push(next);
        onChange({ fieldMappings: arr });
      });
    },
    [state.fieldMappings, onChange],
  );

  const dismissSuggestion = useCallback((s: MappingSuggestion) => {
    // Compute next set and persist so this rejection survives a hard refresh
    // and prevents the column from re-appearing on the next AI re-run.
    const next = new Set([...dismissed, ...getSuggestionSourceColumns(s)]);
    setDismissed(next);
    onChange({ aiDismissed: Array.from(next) });
  }, [dismissed, onChange]);

  const acceptAllHigh = useCallback(() => {
    const high = pendingSuggestions.filter(s => s.confidence >= 0.8);
    if (high.length === 0) return;
    // Compute and persist new dismissed set so accepted columns don't
    // resurface if the author re-runs AI after bulk-accepting.
    const nextDismissed = new Set(dismissed);
    for (const s of high) {
      for (const col of getSuggestionSourceColumns(s)) nextDismissed.add(col);
    }
    setDismissed(nextDismissed);
    onChange({ aiDismissed: Array.from(nextDismissed) });
    // Deferred: bulk-merge into wizard state off the click path.
    startTransition(() => {
      const working = [...state.fieldMappings];
      for (const s of high) {
        const resolvedTargetField = resolveTargetFieldValue(s.targetField, state.dataMode);
        if (!resolvedTargetField) continue;
        const idx = working.findIndex(m => m.targetField === resolvedTargetField);
        const fm: FieldMapping = {
          targetField: resolvedTargetField,
          sourceColumns: getSuggestionSourceColumns(s),
          isMatchKey: idx >= 0 ? !!working[idx]!.isMatchKey : false,
          transform: {
            type: (s.transform as any) ?? 'none',
            ...(s.concatSeparator ? { separator: s.concatSeparator } : {}),
          },
          confidenceScore: s.confidence,
          mappingSource: getSuggestionMappingSource(s),
        };
        if (idx >= 0) working[idx] = { ...working[idx]!, ...fm };
        else working.push(fm);
      }
      onChange({ fieldMappings: working });
    });
  }, [pendingSuggestions, dismissed, state.dataMode, state.fieldMappings, onChange]);

  // ---- Render ----
  const totalRows = parseResult?.totalRows ?? sampleRows.length;
  const previewRows = useMemo(
    () => sortRowsByCompleteness(sampleRows, availableColumns, PREVIEW_SAMPLE_SIZE),
    [sampleRows, availableColumns],
  );

  // Pre-stringify every preview cell ONCE per parse. Without this,
  // cellToString (which runs JSON.stringify on GIS blobs) would execute
  // 100 * ~100 = ~25,000 times on every render, including every suggestion
  // accept click. This is the single largest contributor to INP on this tab.
  const previewCellStrings = useMemo<PreviewCell[][]>(() => {
    return previewRows.map(row =>
      availableColumns.map(col => {
        const full = cellToString(row?.[col]);
        const truncated = full.length > 80 ? `${full.slice(0, 80)}\u2026` : full;
        return { full, truncated };
      }),
    );
  }, [previewRows, availableColumns]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 space-y-1">
          {errors.map(e => (
            <p key={e} className="text-xs text-red-700">
              {e}
            </p>
          ))}
        </div>
      )}

      {/* Intro — only shown before a file is successfully parsed */}
      {!parseResult && (
        <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3">
          <p className="text-xs font-medium text-blue-900">Upload a sample file</p>
          <p className="text-[11px] text-blue-800/80 mt-0.5">
            Drop a representative file (CSV, Excel, GeoPackage, GeoJSON, JSON, or
            zipped shapefile). We&apos;ll show you the first {PREVIEW_SAMPLE_SIZE}{' '}
            rows so you can verify the data, then auto-detect likely column
            matches using AI + rule-based matching. You can accept or tweak the
            matches on the next step.
          </p>
        </div>
      )}

      {/* Drop zone */}
      {parseFile && (
        parseResult && !fileParseError ? (
          /* Compact bar: file parsed — slim single-row display */
          <div
            role="button"
            tabIndex={0}
            aria-label="File uploaded — drag another file here or click to replace"
            onDragOver={handleDropZoneDragOver}
            onDragEnter={handleDropZoneDragOver}
            onDragLeave={handleDropZoneDragLeave}
            onDrop={handleDropZoneDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={`group flex items-center gap-2.5 rounded-lg border px-3 py-1.5 cursor-pointer transition-colors focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
              isDragOver
                ? 'border-blue-400 bg-blue-50'
                : 'border-emerald-200 bg-emerald-50/60 hover:border-emerald-300 hover:bg-emerald-50'
            }`}
          >
            {isDragOver ? (
              <FileUp className="h-4 w-4 text-blue-500 shrink-0" />
            ) : (
              <FileCheck2 className="h-4 w-4 text-emerald-600 shrink-0" />
            )}
            <span className="text-xs font-medium text-emerald-800 truncate min-w-0">
              {sampleFile?.name ?? 'File parsed'}
            </span>
            {sampleFile?.name && (
              <span className="shrink-0 font-mono text-[11px] uppercase text-emerald-600/60">
                .{sampleFile.name.split('.').pop()}
              </span>
            )}
            <span className="shrink-0 text-emerald-300 text-[11px] select-none">·</span>
            <span className="shrink-0 text-[11px] text-emerald-700/70">
              {parseResult.columns.length.toLocaleString()} col{parseResult.columns.length !== 1 ? 's' : ''}
              <span className="mx-1 text-emerald-300">·</span>
              {(parseResult.totalRows ?? previewRows.length).toLocaleString()} rows
            </span>
            <span className="text-[11px] text-emerald-500 shrink-0 ml-auto opacity-60 group-hover:opacity-100 transition-opacity">
              {isDragOver ? 'Drop to replace' : 'Click to replace'}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls,.gpkg,.geojson,.zip,.json"
              onChange={handleFileInput}
            />
          </div>
        ) : (
          /* Full drop zone: no file yet, loading, or parse error */
          <div
            role="button"
            tabIndex={0}
            aria-label="Upload a sample file by dragging it here or clicking to browse"
            onDragOver={handleDropZoneDragOver}
            onDragEnter={handleDropZoneDragOver}
            onDragLeave={handleDropZoneDragLeave}
            onDrop={handleDropZoneDrop}
            onClick={() => !fileParseLoading && fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !fileParseLoading) {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={`group relative overflow-hidden rounded-xl border-2 border-dashed px-6 py-8 text-center transition-all duration-200 ease-out cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              fileParseLoading
                ? 'border-blue-300 bg-blue-50/50 cursor-wait'
                : isDragOver
                ? 'border-blue-500 bg-blue-50 scale-[1.01] shadow-md'
                : fileParseError
                ? 'border-red-300 bg-red-50/60 hover:border-red-400'
                : 'border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/40 hover:shadow-xs'
            }`}
          >
            <div className="flex flex-col items-center justify-center gap-3">
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-full transition-all duration-200 ${
                  fileParseLoading
                    ? 'bg-blue-100 text-blue-600'
                    : isDragOver
                    ? 'bg-blue-100 text-blue-600 scale-110'
                    : fileParseError
                    ? 'bg-red-100 text-red-600'
                    : 'bg-white text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-600 group-hover:scale-105'
                }`}
              >
                {fileParseLoading ? (
                  <Loader2 className="h-7 w-7 animate-spin" />
                ) : isDragOver ? (
                  <FileUp className="h-7 w-7 animate-bounce" />
                ) : (
                  <Upload className="h-7 w-7 transition-transform duration-200 group-hover:-translate-y-0.5" />
                )}
              </div>

              <div className="space-y-1">
                {fileParseLoading ? (
                  <>
                    <p className="text-sm font-semibold text-blue-700">
                      {parseProgress?.kind === 'upload'
                        ? `Uploading ${parseProgress.pct}%`
                        : parseProgress?.kind === 'job'
                          ? parseProgress.status === 'queued'
                            ? 'Queued for parsing…'
                            : parseProgress.status === 'running'
                              ? 'Parsing your file…'
                              : `Parsing (${parseProgress.status})…`
                          : 'Starting upload…'}
                    </p>
                    <p className="text-[11px] text-blue-800/70">
                      Large geo files may take a few minutes. You can switch tabs&mdash;we&apos;ll keep going in the background.
                    </p>
                  </>
                ) : isDragOver ? (
                  <p className="text-sm font-semibold text-blue-700">Release to upload</p>
                ) : fileParseError ? (
                  <>
                    <p className="text-sm font-semibold text-red-700">Couldn&apos;t parse that file</p>
                    <p className="text-xs text-red-600">{fileParseError}</p>
                    <p className="text-xs text-gray-500 pt-1">Drop another file or click to try again</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-gray-800">
                      Drag &amp; drop a sample file here
                    </p>
                    <p className="text-xs text-gray-600">
                      or <span className="text-blue-600 font-medium group-hover:underline">click to browse</span>
                    </p>
                  </>
                )}
              </div>

              {!fileParseLoading && !isDragOver && (
                <p className="text-[11px] text-gray-400">
                  Supports CSV, Excel (.xlsx, .xls), GeoPackage, GeoJSON, JSON, and zipped shapefiles
                </p>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls,.gpkg,.geojson,.zip,.json"
              onChange={handleFileInput}
            />
          </div>
        )
      )}

      {/* Sheet selector */}
      {parseResult && (parseResult.availableSheets ?? []).length > 1 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <label className="text-xs font-semibold text-amber-800 shrink-0">Sheet:</label>
          <select
            className="border border-amber-300 rounded-md px-2 py-1 text-xs text-gray-800 bg-white focus:outline-hidden focus:ring-1 focus:ring-amber-400"
            value={parseResult.activeSheet ?? ''}
            onChange={e => handleSheetChange(e.target.value)}
            disabled={fileParseLoading}
          >
            {(parseResult.availableSheets ?? []).map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-amber-700">
            {(parseResult.availableSheets ?? []).length} sheets detected &mdash; select which to use for mapping.
          </span>
          {fileParseLoading && <Loader2 className="h-3 w-3 animate-spin text-amber-500" />}
        </div>
      )}

      {/* Detected matches block */}
      {availableColumns.length > 0 && (
        <DetectedMatchesBlock
          suggestions={pendingSuggestions}
          loading={suggestLoading}
          error={suggestError}
          onAccept={acceptSuggestion}
          onDismiss={dismissSuggestion}
          onAcceptAllHigh={acceptAllHigh}
          onRerun={() => runAutoSuggest(availableColumns, sampleRows)}
          hasSuggester={!!suggestMappings}
          totalMapped={state.fieldMappings.filter(m => m.sourceColumns?.[0]).length}
          totalColumns={availableColumns.length}
          dataMode={state.dataMode}
        />
      )}

      {/* 100-row sample preview — intentionally receives ONLY data that
          changes when the file is re-parsed. No mapping/accept state flows
          in here, so the table never re-renders on AI accept/dismiss. */}
      {previewRows.length > 0 && availableColumns.length > 0 && (
        <div className="flex-1 min-h-0">
          <SamplePreviewTable
            columns={availableColumns}
            cells={previewCellStrings}
            previewCount={previewRows.length}
            totalRows={totalRows}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SamplePreviewTable
// ---------------------------------------------------------------------------
// Extracted + memoized so that suggestion accept/dismiss clicks (which
// mutate fieldMappings on the parent and re-render the tab) do NOT
// re-render the 100-row x ~100-col preview table unless its inputs
// actually changed. Without this, a single green-check click was
// re-rendering ~25,000 <td> nodes and dominating INP.
// ---------------------------------------------------------------------------
interface SamplePreviewTableProps {
  columns: string[];
  cells: PreviewCell[][];
  previewCount: number;
  totalRows: number;
}

// ---------------------------------------------------------------------------
// Virtualized, self-contained sample grid.
//
// Why virtualize:
//   A 100-row x ~70-col preview is ~17,500 <td> nodes. Mounting them all
//   forced the browser to pay style + layout + paint cost on the full
//   subtree for every scroll/hover/resize — the click-stall symptom the
//   user was seeing. Virtualization keeps the DOM at ~30 rows x ~70 cols
//   (~2,100 cells) regardless of the full sample size.
//
// How it works:
//   - Outer box is flex-column: header bar + scroll viewport + footer bar.
//   - Scroll viewport has BOTH axes scrolling inside; the grid is fully
//     self-contained (page doesn't scroll when you pan the grid).
//   - Fixed ROW_HEIGHT lets us compute a visible row window from scrollTop
//     without measuring every row.
//   - Top/bottom spacer <tr>s preserve the full scrollbar range so the
//     scrollbar thumb stays accurate and keyboard / PageDown behave.
//   - rAF-throttled scroll handler avoids setState per scroll event.
//
// Why it's still memo'd:
//   Scroll-driven re-renders are local to this component. Parent AI
//   accept/dismiss flows never reach this tree because `columns`, `cells`,
//   `previewCount`, `totalRows` are all stable across those events.
// ---------------------------------------------------------------------------
const ROW_HEIGHT = 28;
const ROW_OVERSCAN = 8;

const SamplePreviewTable = memo(function SamplePreviewTable({
  columns,
  cells,
  previewCount,
  totalRows,
}: SamplePreviewTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(180);

  // Track the scroll container's height so the visible window matches
  // what the user can actually see. ResizeObserver keeps this accurate
  // when the user resizes the window or collapses the AI block above.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setViewportH(el.clientHeight);
    const ro = new ResizeObserver(() => setViewportH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // rAF-throttle scroll → at most one setState per frame. Without this,
  // a flick scroll can fire scroll handlers 60+ times in a single tick.
  const rafRef = useRef<number>(0);
  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop;
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      setScrollTop(top);
    });
  }, []);
  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const totalCount = cells.length;
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - ROW_OVERSCAN);
  const endIdx = Math.min(
    totalCount,
    Math.ceil((scrollTop + viewportH) / ROW_HEIGHT) + ROW_OVERSCAN,
  );
  const topPad = startIdx * ROW_HEIGHT;
  const bottomPad = Math.max(0, (totalCount - endIdx) * ROW_HEIGHT);
  const colSpan = columns.length + 1;
  const visible = cells.slice(startIdx, endIdx);

  return (
    <div
      className="flex h-full min-h-[180px] flex-col border border-gray-200 rounded-lg overflow-hidden bg-white"
    >
      {/* Grid header bar — always visible */}
      <div className="shrink-0 flex items-center justify-between px-3 py-1.5 border-b border-gray-200 bg-gray-50">
        <p className="text-xs font-medium text-gray-700">Sample preview</p>
        <p className="text-[11px] text-gray-500">Scroll inside the grid</p>
      </div>

      {/* Scroll viewport — both axes scroll here, not the page */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-scroll"
      >
        <table className="min-w-full text-xs text-left border-separate border-spacing-0">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th className="px-2 font-medium text-gray-500 w-12 text-right border-b border-gray-200 bg-gray-100" style={{ height: ROW_HEIGHT }}>
                #
              </th>
              {columns.map(col => (
                <th
                  key={col}
                  className="px-2 font-medium whitespace-nowrap text-gray-700 border-b border-gray-200 bg-gray-100"
                  title={col}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {topPad > 0 && (
              <tr aria-hidden style={{ height: topPad }}>
                <td colSpan={colSpan} />
              </tr>
            )}
            {visible.map((row, i) => {
              const absIdx = startIdx + i;
              return (
                <tr
                  key={absIdx}
                  style={{ height: ROW_HEIGHT }}
                  className={absIdx % 2 === 1 ? 'bg-gray-50/50' : undefined}
                >
                  <td className="px-2 text-gray-400 text-right font-mono">
                    {absIdx + 1}
                  </td>
                  {row.map((cell, j) => (
                    <td
                      key={columns[j]}
                      className="px-2 text-gray-700 font-mono whitespace-nowrap max-w-[240px] truncate"
                      title={cell.full}
                    >
                      {cell.truncated}
                    </td>
                  ))}
                </tr>
              );
            })}
            {bottomPad > 0 && (
              <tr aria-hidden style={{ height: bottomPad }}>
                <td colSpan={colSpan} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Grid footer bar — always visible, pinned to the box, not the page */}
      <div className="shrink-0 flex items-center justify-between px-3 py-1.5 border-t border-gray-200 bg-gray-50 text-[11px] text-gray-600">
        <span>
          {Math.min(previewCount, PREVIEW_SAMPLE_SIZE).toLocaleString()} rows sampled
          <span className="text-gray-400"> of {totalRows.toLocaleString()} total</span>
        </span>
        <span className="text-gray-400">{columns.length} columns</span>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// DetectedMatchesBlock
// ---------------------------------------------------------------------------

interface DetectedMatchesBlockProps {
  suggestions: MappingSuggestion[];
  loading: boolean;
  error: string | null;
  onAccept: (s: MappingSuggestion) => void;
  onDismiss: (s: MappingSuggestion) => void;
  onAcceptAllHigh: () => void;
  onRerun: () => void;
  hasSuggester: boolean;
  totalMapped: number;
  totalColumns: number;
  dataMode: ImportDataMode;
}

function DetectedMatchesBlock({
  suggestions,
  loading,
  error,
  onAccept,
  onDismiss,
  onAcceptAllHigh,
  onRerun,
  hasSuggester,
  totalMapped,
  totalColumns,
  dataMode,
}: DetectedMatchesBlockProps) {
  // Collapsible: authors working through a large match list on a slow machine
  // want to hide the panel while scrolling the sample preview. Default open
  // so the high-confidence matches are visible on first parse.
  const [collapsed, setCollapsed] = useState(false);
  if (!hasSuggester) return null;
  const hasHigh = suggestions.some(s => s.confidence >= 0.8);

  return (
    <div className="shrink-0 rounded-lg border border-gray-200 bg-white">
      <div className="px-3 py-2 flex items-center justify-between gap-3 border-b border-gray-100">
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand detected matches' : 'Collapse detected matches'}
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80 focus:outline-hidden focus:ring-2 focus:ring-orange-400 rounded-sm"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
          )}
          <Sparkles className="h-4 w-4 text-orange-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-800">
              Detected column matches
              {collapsed && suggestions.length > 0 && (
                <span className="ml-2 text-[11px] font-normal text-gray-500">
                  ({suggestions.length} pending)
                </span>
              )}
            </p>
            {!collapsed && (
              <p className="text-[11px] text-gray-500">
                {loading
                  ? 'Analyzing your columns against known target fields\u2026'
                  : suggestions.length === 0
                  ? totalMapped > 0
                    ? `${totalMapped} of ${totalColumns} columns mapped \u2014 no more confident matches detected.`
                    : 'No confident matches detected. Define mappings by hand on the next step.'
                  : `${suggestions.length} match${suggestions.length === 1 ? '' : 'es'} pending review \u2014 accept to pre-fill the Field Mappings tab.`}
              </p>
            )}
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {hasHigh && !loading && !collapsed && (
            <button
              type="button"
              onClick={onAcceptAllHigh}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-md"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Accept all high
            </button>
          )}
          {!collapsed && (
            <button
              type="button"
              onClick={onRerun}
              disabled={loading}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-60 rounded-md"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {loading ? 'Detecting\u2026' : 'Re-run detection'}
            </button>
          )}
        </div>
      </div>
      {!collapsed && error && (
        <p className="px-3 py-2 text-xs text-red-600 border-b border-gray-100">{error}</p>
      )}
      {!collapsed && suggestions.length > 0 && (
        <ul className="max-h-72 overflow-y-auto overscroll-contain divide-y divide-gray-100">
          {suggestions.map((s, index) => {
            const cols = getSuggestionSourceColumns(s);
            const showConcatStyle = s.transform === 'concat' && cols.length > 1;
            const sourceLabel = showConcatStyle
              ? cols.join(s.concatSeparator ?? ' ')
              : cols[0]!;
            return (
              <li key={`${s.targetField}-${index}`} className="px-3 py-2 flex items-center gap-3">
                <ConfidenceBadge confidence={s.confidence} />
                <div className="flex-1 min-w-0 text-xs">
                  <span className="font-mono text-gray-800">{sourceLabel}</span>
                  <span className="mx-1.5 text-gray-400">&rarr;</span>
                  <span className="font-medium text-gray-700">
                    {getTargetFieldLabel(s.targetField, dataMode)}
                  </span>
                  {s.transform && s.transform !== 'none' && (
                    <span className="ml-1.5 text-[11px] text-gray-400">({s.transform})</span>
                  )}
                  {s.reasoning && (
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate" title={s.reasoning}>
                      {s.reasoning}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => onAccept(s)}
                    aria-label={`Accept ${sourceLabel} -> ${s.targetField}`}
                    className="p-1 rounded-sm text-green-600 hover:bg-green-100"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDismiss(s)}
                    aria-label={`Dismiss ${sourceLabel}`}
                    className="p-1 rounded-sm text-gray-400 hover:bg-gray-200"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
